import _ from 'lodash';
import { createAction } from '~reduxHelpers';
import { addToast } from '~features/toast/ducks';
import {
  approve,
  cancelOrder,
  completeOrder,
  saveNickNameToSpace,
  fetchOrder as fetchOrderAPI,
  fetchTasks as fetchTasksAPI,
} from '~services/api.helpers';

import { statusOptions } from '../reducers/transferReducers';

const getStatusString = status => {
  // One status has several payload statuses and we need to merge these
  const statuses = new Set();

  // Status is actually an array of statuses as seen in the UI
  status.forEach(uiStatus => {
    const payload = statusOptions.find(s => s.value === uiStatus)?.payload;
    payload.forEach(s => statuses.add(s));
  });

  return Array.from(statuses).join(',');
};

// Actions ============================================================

/* eslint-disable max-len */

// TODO: group these by category => and eventually refactor into own features
export const orderCanceled = createAction('ORDER_CANCELED');
export const orderApproved = createAction('ORDER_APPROVED');
export const closeModal = createAction('CLOSE_CONFIRMATION');
export const closeNotification = createAction('HIDE_NOTIFICATION');
export const updateTaskList = createAction('UPDATE_TASKS');
export const showCancelConfirmation = createAction('CONFIRM_CANCEL');
export const abortFetchingTasks = createAction('ABORT_FETCHING_TASKS');
export const setFetchingTasks = createAction('SET_FETCHING_TASKS');
export const receiveSpaces = createAction('RECEIVE_SPACES');
export const addToAddableRows = createAction('ADD_TO_ADDABLE_LIST_OPTIONS');
export const receiveTaskData = createAction('TASKLIST_FETCHED');
export const orderCompleted = createAction('ORDER_COMPLETED');
export const closeCompleteModal = createAction('CLOSE_CONFIRMATION_COMPLETE');
export const showCompleteConfirmation = createAction('CONFIRM_COMPLETE');
export const toggleOnlyOwnAction = createAction('TOGGLE_ONLY_OWN');
export const setIsSearched = createAction('SET_IS_SEARCHED');

export const setPageOffset = createAction('SET_PAGE_OFFSET');

export const setSearchTerm = createAction('SET_SEARCH_TERM');
export const setStartDate = createAction('SET_START_DATE');
export const setEndDate = createAction('SET_END_DATE');
export const setAdvanced = createAction('SET_ADVANCED');
export const setCategory = createAction('SET_CATEGORY');
export const setMultiCategory = createAction('SET_MULTI_CATEGORY');
export const setStatus = createAction('SET_STATUS');
export const setSort = createAction('SET_SORT');
export const setUnit = createAction('SET_UNIT');
export const setCostCenter = createAction('SET_COST_CENTER');
// export const setInitialized = createAction('SET_INITIALIZED');
export const resetFilters = createAction('RESET_FILTERS');

/* eslint-enable max-len */

// Thunks ============================================================

export function fetchTasks(opts = {}) {
  return async (dispatch, getState) => {
    try {
      const {
        transferReducers: {
          category,
          multiCategory,
          status,
          sort,
          unit,
          costCenter,
          searchTerm,
          startDate,
          endDate,
          advanced,
          pageOffset,
        },
      } = getState();

      const options = {
        categories: advanced
          ? multiCategory?.map(c => c.value) || []
          : category?.payload || [],
        statuses: getStatusString(status),
        criteria: sort?.value,
        units: unit?.payload || [],
        costCenters: costCenter?.payload || [],
        startDate,
        endDate,
        orderId: searchTerm === '' ? null : searchTerm,
        myOrders: unit?.value === 'OWN' || unit?.value === 'ALL',
        pageOffset: opts.pageOffset || pageOffset, // Can override this from props
      };

      dispatch(setFetchingTasks(true));
      const tasks = await fetchTasksAPI(options);
      dispatch(receiveTaskData({ data: tasks })); // isApprover????
    } catch (e) {
      console.error('Error in fetchTasks', e);
      dispatch(abortFetchingTasks(false));
      dispatch(
        addToast({
          message: 'Tilausten hakeminen epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

// This is used to fetch a task after making a comment
// it's a bit hacky
export function fetchTask(id) {
  return async (dispatch, getState) => {
    try {
      const { taskData } = getState().transferReducers;

      // If we're on the individual task page, this will be null
      if (!taskData) {
        //  window.location.reload();
        dispatch(
          addToast({
            message: 'Kommentti lähetetty',
            type: 'INFO',
          })
        );
        return;
      }

      const order = await fetchOrderAPI(id);

      const contentClone = _.cloneDeep(taskData.content);
      const currentTaskId = contentClone.findIndex(x => x.id === id);
      contentClone[currentTaskId] = order;

      dispatch(
        receiveTaskData({ data: { ...taskData, content: contentClone } })
      );
      dispatch(setIsSearched(true));
    } catch (e) {
      console.error('Error in fetchTask', e);
      dispatch(
        addToast({
          message: 'Tilauksen hakeminen epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

// FIXME:
export function cancelOrderAndFetchTasks(id, cancelReason) {
  return async dispatch => {
    try {
      const orders = await cancelOrder(id, cancelReason);
      dispatch(orderCanceled(orders));
      dispatch(addToast({ message: 'Tilaus peruttu' }));
    } catch (e) {
      console.error('Error in cancelOrderAndFetchTasks', e);
      dispatch(
        addToast({
          message: 'Tilauksen peruuttaminen epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

export function completeOrderAndFetchTasks(id) {
  return async dispatch => {
    try {
      const orders = await completeOrder(id);
      dispatch(orderCompleted(orders));
      dispatch(addToast({ message: 'Tilaus merkitty valmiiksi' }));
    } catch (e) {
      console.error('Error in completeOrderAndFetchTasks', e);
      dispatch(
        addToast({
          message: 'Tilauksen merkitseminen valmiiksi epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

export function saveSpaceNickName({ space, orgId, nickname }) {
  return async (dispatch, getState) => {
    try {
      await saveNickNameToSpace(orgId, space, nickname);
      const {
        transferReducers: { spaces },
      } = getState();

      // Update spaces to reflect the new nickname
      const newSpaces = spaces.map(x =>
        x.room === space.room && x.hospital === space.hospital
          ? { ...space, nickname }
          : x
      );

      dispatch(receiveSpaces(newSpaces));
      dispatch(addToast({ message: 'Tilatunniste on nyt vaihdettu' }));
    } catch (e) {
      console.error('Error in saveSpaceNickName', e);
      dispatch(
        addToast({
          message: 'Tilatunnisteen päivittäminen epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

export function approveOrder(id) {
  return async dispatch => {
    try {
      const orders = await approve(id);
      dispatch(orderApproved(orders));
      dispatch(addToast({ message: 'Tilaus hyväksytty' }));
    } catch (e) {
      console.error('Error in approveOrder', e);
      dispatch(
        addToast({
          message: 'Tilauksen hyväksyminen epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };
}

export function toggleOnlyOwn() {
  return async dispatch => {
    try {
      dispatch(toggleOnlyOwnAction());
      dispatch(updateTaskList());
    } catch (e) {
      console.log(e);
    }
  };
}

export function setPage(page) {
  return async dispatch => {
    try {
      dispatch(setPageOffset(page));
      dispatch(fetchTasks({ pageOffset: page }));
    } catch (e) {
      console.log(e);
    }
  };
}
